import React, { useState } from 'react';
import { StoreSettings } from '../types';
import { 
  Store, 
  CreditCard, 
  Monitor, 
  Lock, 
  Save, 
  Database, 
  Printer, 
  Trash2,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

interface SettingsProps {
  settings: StoreSettings;
  onUpdateSettings: (newSettings: StoreSettings) => void;
}

const Settings: React.FC<SettingsProps> = ({ settings, onUpdateSettings }) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'financial' | 'system' | 'security'>('profile');
  const [formData, setFormData] = useState<StoreSettings>(settings);
  const [isSaved, setIsSaved] = useState(false);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdateSettings(formData);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
  };

  const tabs = [
    { id: 'profile', label: 'Store Profile', icon: <Store size={18} /> },
    { id: 'financial', label: 'Financials', icon: <CreditCard size={18} /> },
    { id: 'system', label: 'System', icon: <Monitor size={18} /> },
    { id: 'security', label: 'Security', icon: <Lock size={18} /> },
  ];

  return (
    <div className="w-full h-full space-y-6">
      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Tab Sidebar - Fixed width on large screens, full width on mobile */}
        <div className="w-full lg:w-72 flex flex-col gap-1 sticky top-24">
          <div className="mb-4 px-4">
            <h2 className="text-sm font-black text-slate-400 uppercase tracking-[0.2em]">Configuration</h2>
          </div>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-4 px-6 py-4 rounded-2xl font-bold transition-all duration-200 ${
                activeTab === tab.id 
                  ? 'bg-emerald-600 text-white shadow-xl shadow-emerald-600/30 translate-x-1' 
                  : 'text-slate-500 hover:bg-white hover:text-emerald-600 hover:shadow-sm'
              }`}
            >
              <span className={`${activeTab === tab.id ? 'text-white' : 'text-slate-400'}`}>
                {tab.icon}
              </span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Area - Expanded to fill remaining space */}
        <div className="flex-1 w-full bg-white rounded-[2.5rem] shadow-sm border border-slate-200 overflow-hidden flex flex-col min-h-[600px]">
          <form onSubmit={handleSave} className="flex flex-col h-full">
            <div className="p-10 flex-1">
              {activeTab === 'profile' && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="border-b border-slate-100 pb-6 mb-8">
                    <h3 className="text-2xl font-black text-slate-900">Store Profile</h3>
                    <p className="text-slate-500 mt-1">Manage your pharmacy's identity and contact details displayed on invoices.</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Pharmacy Name</label>
                      <div className="relative group">
                        <Store className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-emerald-500 transition-colors" size={20} />
                        <input 
                          type="text" 
                          required
                          className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all font-semibold"
                          value={formData.storeName}
                          onChange={e => setFormData({...formData, storeName: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Official Contact</label>
                      <input 
                        type="text" 
                        required
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all font-semibold"
                        value={formData.phone}
                        onChange={e => setFormData({...formData, phone: e.target.value})}
                      />
                    </div>
                    
                    <div className="space-y-2 col-span-full">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Business Email</label>
                      <input 
                        type="email" 
                        required
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all font-semibold"
                        value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2 col-span-full">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Full Business Address</label>
                      <textarea 
                        required
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all resize-none font-semibold"
                        rows={4}
                        value={formData.address}
                        onChange={e => setFormData({...formData, address: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'financial' && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="border-b border-slate-100 pb-6 mb-8">
                    <h3 className="text-2xl font-black text-slate-900">Financial & Logic</h3>
                    <p className="text-slate-500 mt-1">Configure global pricing rules, taxes, and inventory alert systems.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1 text-emerald-600">Currency Identifier</label>
                      <input 
                        type="text" 
                        required
                        className="w-full px-5 py-4 bg-emerald-50/30 border border-emerald-100 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all font-black text-emerald-600 text-xl"
                        value={formData.currency}
                        onChange={e => setFormData({...formData, currency: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Standard Sales Tax (%)</label>
                      <input 
                        type="number" 
                        required
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all font-semibold"
                        value={formData.taxRate}
                        onChange={e => setFormData({...formData, taxRate: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div className="space-y-2 col-span-full">
                      <div className="bg-red-50 p-6 rounded-3xl border border-red-100">
                        <label className="text-[11px] font-black text-red-400 uppercase tracking-widest block mb-2">Inventory Health: Low Stock Threshold</label>
                        <div className="flex items-center gap-4">
                          <input 
                            type="number" 
                            required
                            className="flex-1 px-5 py-4 bg-white border border-red-200 rounded-2xl focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none transition-all text-red-600 font-black text-2xl"
                            value={formData.lowStockThreshold}
                            onChange={e => setFormData({...formData, lowStockThreshold: parseInt(e.target.value) || 0})}
                          />
                          <div className="hidden sm:block">
                            <p className="text-xs text-red-500/70 font-medium leading-tight">
                              Items with quantity below this value <br/> will trigger high-priority alerts.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'system' && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="border-b border-slate-100 pb-6 mb-8">
                    <h3 className="text-2xl font-black text-slate-900">System Preferences</h3>
                    <p className="text-slate-500 mt-1">Configure automated behaviors and invoice customization.</p>
                  </div>

                  <div className="space-y-8">
                    <div className="flex items-center justify-between p-6 bg-slate-50 rounded-[2rem] border border-slate-100 hover:border-emerald-200 transition-colors group">
                      <div className="flex items-center gap-5">
                        <div className="p-4 bg-white rounded-2xl shadow-sm group-hover:text-emerald-600 transition-colors">
                          <Printer size={28} />
                        </div>
                        <div>
                          <p className="text-lg font-black text-slate-800">Automatic Receipt Printing</p>
                          <p className="text-sm text-slate-500">Automatically open the print dialog immediately after a sale is finalized.</p>
                        </div>
                      </div>
                      <button 
                        type="button"
                        onClick={() => setFormData({...formData, autoPrintReceipt: !formData.autoPrintReceipt})}
                        className={`w-16 h-8 rounded-full relative transition-all duration-300 ${formData.autoPrintReceipt ? 'bg-emerald-500 shadow-lg shadow-emerald-500/30' : 'bg-slate-300'}`}
                      >
                        <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-all duration-300 ${formData.autoPrintReceipt ? 'right-1' : 'left-1'}`} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Receipt Branding Header</label>
                        <input 
                          type="text" 
                          placeholder="e.g. Abdullah Medical Store - Serving Health"
                          className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all font-semibold"
                          value={formData.receiptHeader}
                          onChange={e => setFormData({...formData, receiptHeader: e.target.value})}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Receipt Customer Note</label>
                        <input 
                          type="text" 
                          placeholder="e.g. Items cannot be returned without original receipt"
                          className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all font-semibold"
                          value={formData.receiptFooter}
                          onChange={e => setFormData({...formData, receiptFooter: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="pt-8 border-t border-slate-100">
                      <div className="flex items-center gap-2 mb-6">
                        <AlertCircle className="text-red-500" size={18} />
                        <h4 className="text-[11px] font-black text-red-500 uppercase tracking-[0.2em]">Maintenance & Data Safety</h4>
                      </div>
                      <div className="flex flex-wrap gap-4">
                        <button type="button" className="flex items-center gap-3 px-6 py-3 bg-white border-2 border-slate-100 text-slate-700 rounded-2xl text-sm font-black hover:bg-slate-50 hover:border-slate-200 transition-all">
                          <Database size={18} /> Export Encrypted Backup
                        </button>
                        <button type="button" className="flex items-center gap-3 px-6 py-3 bg-red-50 text-red-600 rounded-2xl text-sm font-black hover:bg-red-100 transition-all border border-red-100">
                          <Trash2 size={18} /> Wipe System Data
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="border-b border-slate-100 pb-6 mb-8">
                    <h3 className="text-2xl font-black text-slate-900">Security & Authentication</h3>
                    <p className="text-slate-500 mt-1">Update administrative credentials and manage system access.</p>
                  </div>

                  <div className="max-w-xl space-y-6">
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Current Password</label>
                      <input 
                        type="password" 
                        placeholder="••••••••"
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">New Administrative Password</label>
                      <input 
                        type="password" 
                        placeholder="••••••••"
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Confirm New Password</label>
                      <input 
                        type="password" 
                        placeholder="••••••••"
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sticky Footer Action Bar */}
            <div className="p-8 bg-slate-50/80 backdrop-blur-md border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                {isSaved ? (
                  <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 px-4 py-2 rounded-full border border-emerald-100 animate-in fade-in slide-in-from-left-4">
                    <CheckCircle2 size={16} />
                    <span className="text-sm font-bold">Configurations Updated</span>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 font-medium">Please review all changes before committing to system update.</p>
                )}
              </div>
              <button 
                type="submit"
                className="w-full sm:w-auto flex items-center justify-center gap-3 px-12 py-4 bg-emerald-600 text-white rounded-2xl font-black text-base shadow-xl shadow-emerald-600/20 hover:bg-emerald-700 hover:shadow-2xl hover:shadow-emerald-600/40 active:scale-[0.98] transition-all duration-300"
              >
                <Save size={20} />
                Save All Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;